/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import placeholder from "../../assets/images/no_image_placeholder.jpg";
import { getMPSEventAppliedStatus } from "../../firebaseFunctions/getMPSEventAppliedStatus/getMPSEventAppliedStatus";
import { eventDiscoveryService } from "../../services/EventDiscoveryService";
import { StatusBadge } from "../ui/TableComponents";
import { Sun, Moon, Globe, FileCheck, X } from "lucide-react";
import {
  getEventRequiredDocuments,
  getVendorAcknowledgment,
  getVendorSignature,
} from "../../services/EventDocumentService";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import ScrollIndicator from "../ui/ScrollIndicator";

const DashboardContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const MPSProfileID = user.uid;
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLanguage = i18n.language === "en" ? "es" : "en";
    i18n.changeLanguage(newLanguage);
  };
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [recommendedEvents, setRecommendedEvents] = useState([]);
  const [appliedToEvents, setAppliedToEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState("");
  const [filteredRecommendedEvents, setFilteredRecommendedEvents] = useState(
    [],
  );
  const [activeFilter, setActiveFilter] = useState("All");
  const [filteredAppliedEvents, setFilteredAppliedEvents] = useState([]);
  const [pendingDocsCount, setPendingDocsCount] = useState(0);
  const [pendingDocsBannerDismissed, setPendingDocsBannerDismissed] = useState(
    false,
  );

  useEffect(() => {
    const loadAllEvents = async () => {
      setLoading(true);
      try {
        // Use the same data source as /mps/events for consistency
        const result = await eventDiscoveryService.discoverEvents(MPSProfileID);

        if (result.success) {
          const allDiscoveredEvents = result.allEvents.map((event) => ({
            ...event,
            image: event.image || placeholder,
          }));
          setRecommendedEvents(allDiscoveredEvents);
          setFilteredRecommendedEvents(allDiscoveredEvents);
        }

        const appliedStatus = await getMPSEventAppliedStatus(MPSProfileID);
        if (appliedStatus.success) {
          setAppliedToEvents(appliedStatus.eventDetails);
        }
      } catch (error) {
        console.error("Error loading events:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAllEvents();
  }, [MPSProfileID]);

  useEffect(() => {
    if (location.state && location.state.refresh) {
      const loadAllEvents = async () => {
        setLoading(true);
        try {
          const result = await eventDiscoveryService.discoverEvents(
            MPSProfileID,
          );

          if (result.success) {
            const allDiscoveredEvents = result.allEvents.map((event) => ({
              ...event,
              image: event.image || placeholder,
            }));
            setRecommendedEvents(allDiscoveredEvents);
            setFilteredRecommendedEvents(allDiscoveredEvents);
          }

          const appliedStatus = await getMPSEventAppliedStatus(MPSProfileID);
          if (appliedStatus.success) {
            setAppliedToEvents(appliedStatus.eventDetails);
          }
        } catch (error) {
          console.error("Error loading events:", error);
        } finally {
          setLoading(false);
        }
      };
      loadAllEvents();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, MPSProfileID, navigate, location.pathname]);

  useEffect(() => {
    const categorized = categorizeEvents(appliedToEvents);
    setFilteredAppliedEvents(
      categorized.filter((e) => e.eventCategory !== "past"),
    );
  }, [appliedToEvents]);

  // Check for pending required documents across active events
  useEffect(() => {
    if (!appliedToEvents.length || !MPSProfileID) return;

    const checkPendingDocs = async () => {
      try {
        const activeEvents = appliedToEvents.filter((e) => {
          const status = (e.applicationStatus || "").toLowerCase();
          return status === "registered" || status === "approved";
        });

        let count = 0;
        for (const event of activeEvents) {
          const eid = event.eventId || event.id;
          if (!eid) continue;

          const result = await getEventRequiredDocuments(eid);
          if (!result.success) continue;

          const requiredDocs = result.documents.filter(
            (d) => d.isRequired && d.requiresAcknowledgment !== false,
          );

          for (const rdoc of requiredDocs) {
            let done = false;
            if (rdoc.requiresSignature && rdoc.signatureFields?.length > 0) {
              const sigResult = await getVendorSignature(
                eid,
                rdoc.id,
                MPSProfileID,
              );
              done = sigResult.signed;
            } else {
              const ackResult = await getVendorAcknowledgment(
                eid,
                rdoc.id,
                MPSProfileID,
              );
              done = ackResult.acknowledged;
            }
            if (!done) count++;
          }
        }
        setPendingDocsCount(count);
      } catch (e) {
        console.error("Error checking pending docs:", e);
      }
    };

    checkPendingDocs();
  }, [appliedToEvents, MPSProfileID]);

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const navigateMonth = (direction) => {
    setCurrentMonth((prevMonth) => {
      const newMonth = prevMonth + direction;
      if (newMonth < 0) {
        setCurrentYear((prevYear) => prevYear - 1);
        return 11;
      } else if (newMonth > 11) {
        setCurrentYear((prevYear) => prevYear + 1);
        return 0;
      }
      return newMonth;
    });
  };

  const getMyEventDates = () => {
    const eventDates = new Set();
    appliedToEvents.forEach((event) => {
      if (event.startDate) {
        const startDate = new Date(event.startDate);
        const endDate = event.endDate ? new Date(event.endDate) : startDate;
        let current = new Date(startDate);
        while (current <= endDate) {
          eventDates.add(current.toDateString());
          current.setDate(current.getDate() + 1);
        }
      } else if (event.date) {
        eventDates.add(new Date(event.date).toDateString());
      }
    });
    return eventDates;
  };

  const myEventDates = getMyEventDates();

  const renderCalendar = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const firstDayOfWeek = firstDayOfMonth.getDay();

    const calendarDays = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarDays.push(<div key={`empty-${i}`} className="w-10 h-10"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();
      const hasMyEvent = myEventDates.has(date.toDateString());

      calendarDays.push(
        <div
          key={day}
          className={`w-10 h-10 flex flex-col items-center justify-center cursor-pointer rounded-lg text-sm font-medium transition-all
                        ${
                          isSelected
                            ? "bg-primary-500 text-white shadow-md"
                            : hasMyEvent
                            ? "bg-primary-100 text-primary-700 ring-2 ring-primary-300"
                            : isToday
                            ? "bg-grey-800 text-white"
                            : "hover:bg-grey-100 text-grey-700"
                        }`}
          onClick={() => handleDateSelect(date)}
        >
          {day}
          {hasMyEvent && !isSelected && (
            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-0.5"></div>
          )}
        </div>,
      );
    }

    return calendarDays;
  };

  const handleFilterSelect = (filter) => {
    setActiveFilter(filter);

    if (filter === "All") {
      setFilteredAppliedEvents(
        categorizedEvents.filter((e) => e.eventCategory !== "past"),
      );
    } else if (filter === "Happening") {
      setFilteredAppliedEvents(happeningNowEvents);
    } else if (filter === "Upcoming") {
      setFilteredAppliedEvents(upcomingEvents);
    } else if (filter === "Applied") {
      setFilteredAppliedEvents(
        categorizedEvents.filter(
          (e) =>
            (e.applicationStatus === "Applied" ||
              e.applicationStatus === "Pending") &&
            e.eventCategory !== "past",
        ),
      );
    } else if (filter === "Registered") {
      setFilteredAppliedEvents(
        categorizedEvents.filter(
          (e) =>
            e.applicationStatus === "Registered" && e.eventCategory !== "past",
        ),
      );
    }
  };

  const handleLocationSearch = (value) => {
    setSearchLocation(value);
    if (!value.trim()) {
      setFilteredRecommendedEvents(recommendedEvents);
      return;
    }
    const searchTerms = value.toLowerCase();
    const filtered = recommendedEvents.filter((event) => {
      const location = (event.location || "").toLowerCase();
      const address = (event.address || "").toLowerCase();
      return location.includes(searchTerms) || address.includes(searchTerms);
    });
    setFilteredRecommendedEvents(filtered);
  };

  const filterButtons = [
    { key: "All", label: "All" },
    { key: "Happening", label: "Happening Now" },
    { key: "Upcoming", label: "Upcoming" },
    { key: "Applied", label: "Applied" },
    { key: "Registered", label: "Registered" },
  ];

  const categorizeEvents = (eventsList) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return eventsList.map((event) => {
      const startDate = event.startDate ? new Date(event.startDate) : null;
      const endDate = event.endDate ? new Date(event.endDate) : startDate;

      let eventCategory = "upcoming";

      if (startDate && endDate) {
        const eventStart = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
        );
        const eventEnd = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
        );

        if (today >= eventStart && today <= eventEnd) {
          eventCategory = "happening";
        } else if (today > eventEnd) {
          eventCategory = "past";
        } else {
          eventCategory = "upcoming";
        }
      }

      return { ...event, eventCategory };
    });
  };

  const categorizedEvents = categorizeEvents(appliedToEvents);
  const happeningNowEvents = categorizedEvents.filter(
    (e) => e.eventCategory === "happening",
  );
  const upcomingEvents = categorizedEvents.filter(
    (e) => e.eventCategory === "upcoming",
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] transition-opacity duration-300">
        <div className="text-center">
          <p className="text-grey-500 text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto relative">
      <ScrollIndicator storageKey="mps_dashboard_scroll_hint" />
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-grey-900">
            Welcome back {user?.displayName || user?.businessName || "!"}
          </h1>
          <p className="text-grey-500 mt-1">
            Here's what's happening with your events
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg border transition-all ${
              isDarkMode
                ? "bg-grey-800 border-grey-700 text-yellow-400 hover:bg-grey-700"
                : "bg-white border-grey-200 text-grey-600 hover:bg-grey-50"
            }`}
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-grey-200 bg-white hover:bg-grey-50 text-xs font-medium text-grey-600 transition-colors"
            title="Toggle language / Cambiar idioma"
          >
            <Globe className="w-4 h-4" />
            <span>{i18n.language === "en" ? "ES" : "EN"}</span>
          </button>
        </div>
      </div>

      {/* Pending documents alert */}
      {pendingDocsCount > 0 && !pendingDocsBannerDismissed && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-900">
                {pendingDocsCount} document{pendingDocsCount !== 1 ? "s" : ""}{" "}
                need{pendingDocsCount === 1 ? "s" : ""} your attention
              </p>
              <p className="text-sm text-amber-700">
                Review and sign required documents for your events.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => navigate("/mps/events")}
              className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
            >
              Review Now
            </button>
            <button
              onClick={() => setPendingDocsBannerDismissed(true)}
              className="p-1 text-amber-500 hover:text-amber-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-grey-100 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5 text-grey-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="font-semibold text-grey-900">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-grey-100 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5 text-grey-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
              <div
                key={i}
                className="w-10 h-8 flex items-center justify-center text-xs font-medium text-grey-400"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>

          <div className="mt-3 pt-3 border-t border-grey-100 flex items-center gap-4 text-xs text-grey-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-primary-100 rounded ring-1 ring-primary-300"></div>
              <span>My Events</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-grey-800 rounded"></div>
              <span>Today</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-grey-200">
          <div className="flex items-center justify-between p-4 border-b border-grey-100">
            <h2 className="font-semibold text-grey-900">My Events</h2>
            <button
              onClick={() => navigate("/mps/events")}
              className="text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              View All
            </button>
          </div>

          <div className="flex gap-2 p-3 border-b border-grey-100 overflow-x-auto">
            {filterButtons.map((btn) => {
              let count = 0;
              if (btn.key === "All")
                count = categorizedEvents.filter(
                  (e) => e.eventCategory !== "past",
                ).length;
              else if (btn.key === "Happening")
                count = happeningNowEvents.length;
              else if (btn.key === "Upcoming") count = upcomingEvents.length;
              else if (btn.key === "Applied")
                count = categorizedEvents.filter(
                  (e) =>
                    (e.applicationStatus === "Applied" ||
                      e.applicationStatus === "Pending") &&
                    e.eventCategory !== "past",
                ).length;
              else if (btn.key === "Registered")
                count = categorizedEvents.filter(
                  (e) =>
                    e.applicationStatus === "Registered" &&
                    e.eventCategory !== "past",
                ).length;

              return (
                <button
                  key={btn.key}
                  onClick={() => handleFilterSelect(btn.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                    activeFilter === btn.key
                      ? btn.key === "Happening"
                        ? "bg-green-600 text-white"
                        : "bg-grey-900 text-white"
                      : btn.key === "Happening" && count > 0
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-grey-100 text-grey-600 hover:bg-grey-200"
                  }`}
                >
                  {btn.key === "Happening" && count > 0 && (
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  )}
                  {btn.label}
                  {count > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full ${
                        activeFilter === btn.key ? "bg-white/20" : "bg-grey-200"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            {filteredAppliedEvents.length > 0 ? (
              <div className="divide-y divide-grey-100">
                {filteredAppliedEvents.slice(0, 5).map((event, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-4 hover:bg-grey-50 transition-colors cursor-pointer ${
                      event.eventCategory === "happening"
                        ? "bg-green-50 border-l-4 border-green-500"
                        : ""
                    }`}
                    onClick={() =>
                      navigate(`/mps/view-event-details/${event.eventId}`)
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <img
                          src={event?.eventCoverPhoto || placeholder}
                          alt={event.eventName}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        {event.eventCategory === "happening" && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-medium text-grey-900">
                            {event.eventName}
                          </h3>
                          {event.eventCategory === "happening" && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                              LIVE
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-grey-500">
                          {event.eventCategory === "happening"
                            ? "Happening now"
                            : event.date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge
                        status={event.applicationStatus}
                        type="application"
                      />
                      <svg
                        className="w-4 h-4 text-grey-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-12 h-12 bg-grey-100 rounded-full flex items-center justify-center mb-3">
                  <svg
                    className="w-6 h-6 text-grey-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-grey-900 font-medium mb-1">
                  {activeFilter !== "All"
                    ? `No ${activeFilter} events`
                    : "No events yet"}
                </p>
                <p className="text-grey-500 text-sm text-center mb-4">
                  {activeFilter !== "All"
                    ? "Try a different filter"
                    : "Find events to participate in"}
                </p>
                <button
                  onClick={() =>
                    activeFilter !== "All"
                      ? handleFilterSelect("All")
                      : navigate("/mps/events")
                  }
                  className="px-4 py-2 bg-grey-900 text-white text-sm font-medium rounded-lg hover:bg-grey-800 transition-colors"
                >
                  {activeFilter !== "All" ? "Show All" : "Browse Events"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-grey-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-grey-100">
          <h2 className="font-semibold text-grey-900">Discover Events</h2>
          <div className="relative">
            <svg
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-grey-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchLocation}
              onChange={(e) => handleLocationSearch(e.target.value)}
              placeholder="Search by location..."
              className="w-full sm:w-64 pl-10 pr-4 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-300"
            />
          </div>
        </div>

        {filteredRecommendedEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {filteredRecommendedEvents.slice(0, 6).map((event) => (
              <div
                key={event.id}
                className="border border-grey-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/mps/view-event-details/${event.id}`)}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={event.image || placeholder}
                    alt={event.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 right-2">
                    <StatusBadge status={event.status} type="application" />
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-grey-900 mb-1 line-clamp-1">
                    {event.name}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-grey-500 mb-2">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="line-clamp-1">{event.location}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-grey-500">
                      {event.date?.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    <span className="text-primary-500 font-medium">
                      {event.sellerCapacity} spots
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 bg-grey-100 rounded-full flex items-center justify-center mb-3">
              <svg
                className="w-6 h-6 text-grey-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <p className="text-grey-900 font-medium mb-1">No events found</p>
            <p className="text-grey-500 text-sm">Try a different search term</p>
          </div>
        )}

        {filteredRecommendedEvents.length > 6 && (
          <div className="p-4 border-t border-grey-100 text-center">
            <button
              onClick={() => navigate("/mps/events")}
              className="text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              View all {filteredRecommendedEvents.length} events
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardContent;
